'use client';

import { styled } from 'styled-components';
import { ImageProps, SharedHeroProps, VideoProps } from '@types';
import cs from 'classnames';
import * as Styled from '@lib/styles';
import Button from '@dvd/components/Button';
import TextLink from '@dvd/components/TextLink';
import {
  HeroDescription,
  HeroLogos,
  HeroComputedMedia,
  HeroMessaging,
  HeroSubtitle,
  HeroMedia,
} from '@dvd/modules/Heroes/HeroSharedComponents';

interface HeroSplitProps extends SharedHeroProps {
  /**
   * The video to display
   * - **Required** for video content (mutually exclusive with image)
   */
  video?: VideoProps;
  /**
   * Featured image to display
   * - **Required** for image content (mutually exclusive with hashedId for video content)
   * - Only `src` and `alt` properties are required; `width` and `height` are fixed for layout consistency
   */
  image?: Pick<ImageProps, 'src' | 'alt'>;
  /**
   * Layout variant of the hero section. Portrait is typically used for videos and images with a 1:1 aspect ratio. Landscape is typically used for videos and images with a 16:9 aspect ratio..
   */
  variant?: 'portrait' | 'landscape';
  /**
   * Whether to show customer logos
   */
  showLogos?: boolean;
  /**
   * Text to display below the primary CTA button
   * - Only used if primaryCta is provided and no secondary CTA is provided
   */
  primaryCtaSubtext?: string;
}

const HeroContainer = styled.div`
  padding-block-end: ${({ theme }) => theme.spacing[3]};
  padding-block-start: ${({ theme }) => theme.spacing[4]};
  display: grid;
  ${({ theme }) => theme.grid.columns.small}

  &.portrait {
    ${HeroMessaging} {
      grid-column: 1 / span 2;
      grid-row: 1;
      text-align: center;
    }

    ${HeroMedia} {
      grid-column: 1 / span 2;
      grid-row: 2;
    }

    ${HeroLogos} {
      grid-column: 1 / span 2;
      grid-row: 3;
    }
  }

  &.landscape {
    ${HeroMessaging} {
      grid-column: 1 / span 2;
      grid-row: 1;
      text-align: center;
    }

    ${HeroMedia} {
      grid-column: 1 / span 2;
      grid-row: 2;
    }

    ${HeroLogos} {
      grid-column: 1 / span 2;
      grid-row: 3;
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-block-end: ${({ theme }) => theme.spacing[4]};

    &.portrait {
      align-items: center;
      display: grid;
      padding-block-start: ${({ theme }) => theme.spacing[4]};
      ${({ theme }) => theme.grid.columns.large}

      ${HeroMessaging} {
        grid-column: 1 / span 6;
        grid-row: 1;
        text-align: start;
      }

      ${HeroMedia} {
        margin: 0 auto;
        grid-column: 8 / span 5;
        grid-row: 1;
      }

      ${HeroLogos} {
        margin-block-start: ${({ theme }) => theme.spacing[2]};
        grid-column: 1 / span 12;
        grid-row: 2;
        width: 100%;
      }
    }

    &.landscape {
      align-items: center;
      display: grid;
      ${({ theme }) => theme.grid.columns.large}

      ${HeroMessaging} {
        grid-column: 1 / span 5;
        grid-row: 1;
        text-align: start;
      }

      ${HeroMedia} {
        margin: 0 auto;
        grid-column: 6 / span 7;
        grid-row: 1;
      }

      ${HeroLogos} {
        margin-block-start: ${({ theme }) => theme.spacing[2]};
        grid-column: 1 / span 12;
        grid-row: 2;
        width: 100%;
      }
    }
  }
`;

const HeroCtaContainer = styled.div<{ $hasSubtext: boolean }>`
  display: grid;
  grid-template-areas: 'primary' 'subtext' 'secondary';
  width: unset;
  margin: ${({ theme }) => theme.spacing[1.5]} auto 0;

  ${({ theme }) => theme.mq.minWidth.medium} {
    grid-template-areas: ${({ $hasSubtext }) =>
      $hasSubtext
        ? '"primary" "subtext"'
        : '"primary secondary" "subtext subtext"'};
    width: max-content;
    margin: ${({ theme }) => theme.spacing[1.5]} auto 0;
    place-items: center;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin: ${({ theme }) => theme.spacing[1.5]} 0 0;
  }

  // Remove the extra padding we get from the p tag
  p {
    margin-block-end: 0;
  }
`;

const HeroCtaPrimary = styled(Button)`
  grid-area: primary;
  place-self: center;

  ${({ theme }) => theme.mq.minWidth.large} {
    place-self: start;
  }
`;

const HeroCtaSecondary = styled(Button)`
  grid-area: secondary;
  place-self: center;
  margin-block-start: ${({ theme }) => theme.spacing[1.5]};

  ${({ theme }) => theme.mq.minWidth.medium} {
    grid-area: secondary;
    margin-block-start: 0;
    margin-inline-start: ${({ theme }) => theme.spacing[1.5]};
  }
`;

const HeroCtaSubtext = styled(Styled.BodySm)`
  grid-area: subtext;
  place-self: center;
  margin-block-start: ${({ theme }) => theme.spacing[0.5]};

  ${({ theme }) => theme.mq.minWidth.large} {
    place-self: start;
  }
`;

const HeroCtaLink = styled(Styled.BodyLg)`
  grid-area: primary;
`;

/**
 *
 * This Hero displays a split layout for the Hero. The default variant is landscape.
 *
 */
const HeroSplit = ({
  description,
  video = null,
  headline,
  image = null,
  primaryCta = null,
  secondaryCta = null,
  linkCta = null,
  showLogos = true,
  title,
  variant = 'landscape',
  primaryCtaSubtext = null,
}: HeroSplitProps) => {
  const hasCtaSubtext = !!primaryCtaSubtext && !secondaryCta;
  return (
    <HeroContainer className={cs(variant)}>
      <HeroMessaging>
        <header>
          <Styled.Eyebrow as="h1">{title}</Styled.Eyebrow>
          <HeroSubtitle aria-roledescription="subtitle">
            {headline}
          </HeroSubtitle>
        </header>
        <HeroDescription description={description} />

        {primaryCta || secondaryCta || linkCta ? (
          <HeroCtaContainer $hasSubtext={hasCtaSubtext}>
            {primaryCta?.href ? (
              <>
                <HeroCtaPrimary
                  color={primaryCta.buttonColor ?? 'blue500'}
                  href={primaryCta.href}
                  trackingProperty={primaryCta.trackingProperty}
                  trackingLabel={primaryCta.trackingLabel}
                >
                  {primaryCta.text}
                </HeroCtaPrimary>
                {/* Only show the subtext if there is no secondary CTA */}
                {hasCtaSubtext ? (
                  <HeroCtaSubtext>{primaryCtaSubtext}</HeroCtaSubtext>
                ) : null}
              </>
            ) : null}
            {secondaryCta?.href ? (
              <HeroCtaSecondary
                variant="secondary"
                href={secondaryCta.href}
                trackingProperty={secondaryCta.trackingProperty}
                trackingLabel={secondaryCta.trackingLabel}
              >
                {secondaryCta.text}
              </HeroCtaSecondary>
            ) : null}
          </HeroCtaContainer>
        ) : null}
        {/* Needs a separate CtaContainer for the linkCta so it can sit outside of the CtaContainer for the primary and secondary ctas */}
        {linkCta ? (
          <HeroCtaContainer $hasSubtext={false}>
            <HeroCtaLink>
              <TextLink cta={linkCta} />
            </HeroCtaLink>
          </HeroCtaContainer>
        ) : null}
      </HeroMessaging>

      <HeroComputedMedia
        hashedId={video?.hashedId}
        imageAlt={image?.alt}
        imageSrc={image?.src}
        variant={variant}
        aspect={video?.aspect}
      />
      {showLogos ? <HeroLogos /> : null}
    </HeroContainer>
  );
};

export default HeroSplit;
